import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  User,
  FileText,
  Home,
  BookOpen,
  Code,
  Upload,
  History,
} from "lucide-react";
import NotificationsPanel from "./NotificationsPanel";
import "./SIPOTMLayout.css";

function SIPOTMLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Get API Docs URL for SIP/OTM pipeline
  const getSIPOTMApiDocsUrl = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:5001/api/extraction/sip-otm/v1/docs';
    }
    return `${window.location.protocol}//${window.location.hostname}/api/extraction/sip-otm/v1/docs`;
  };

  const menuItems = [
    { path: "/sip-otm", icon: Home, label: "Dashboard", exact: true },
    { path: "/sip-otm/dashboard", icon: Home, label: "Dashboard" },
    { path: "/sip-otm/extract", icon: Upload, label: "Extract Document", color: "#8b5cf6" },
    { path: "/sip-otm/history", icon: History, label: "Work Orders", color: "#6366f1" },
    { path: "/sip-otm/api-logs", icon: Code, label: "API Logs" },
    { path: getSIPOTMApiDocsUrl(), icon: BookOpen, label: "API Docs", external: true },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    if (item.path === "/sip-otm/dashboard" && (location.pathname === "/sip-otm" || location.pathname === "/sip-otm/")) {
      return true;
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    onLogout();
    navigate("/login");
  };

  const getPageTitle = () => {
    if (location.pathname === "/sip-otm" || location.pathname === "/sip-otm/" || location.pathname === "/sip-otm/dashboard") {
      return "SIP/OTM Dashboard";
    }
    if (location.pathname === "/sip-otm/extract") {
      return "Extract Document";
    }
    if (location.pathname === "/sip-otm/history") {
      return "Work Orders";
    }
    if (location.pathname.startsWith("/sip-otm/work-order/")) {
      return "Work Order Details";
    }
    const item = menuItems.find(m => isActive(m) && !m.exact);
    if (item) {
      return item.label;
    }
    return "SIP/OTM Pipeline";
  };

  return (
    <div className="sipotm-layout">
      {/* Sidebar */}
      <aside className="sipotm-sidebar">
        <div className="sipotm-sidebar-header">
          {(user?.pipeline === "all" || user?.role === "superadmin" || user?.role === "admin") && (
            <button 
              onClick={() => navigate("/dashboard")} 
              className="sipotm-back-btn"
              title="Back to Main Dashboard"
            >
              <span>←</span> Main Dashboard
            </button>
          )}
          <div className="sipotm-logo">
            <FileText size={24} className="sipotm-logo-icon" />
            <span className="sipotm-logo-text">SIP/OTM Pipeline</span>
          </div>
          <div className="sipotm-logo-subtitle">SIP & OTM Mandate Extraction</div>
        </div>

        <nav className="sipotm-sidebar-nav">
          {menuItems.filter(item => !item.exact).map((item) => (
            <button
              key={item.path}
              className={`sipotm-nav-item ${isActive(item) ? "active" : ""} ${item.external ? "sipotm-nav-item-external" : ""}`}
              onClick={() => {
                if (item.external) {
                  window.open(item.path, "_blank");
                } else {
                  navigate(item.path);
                }
              }}
              title={item.label}
              style={item.color ? { "--item-color": item.color } : {}}
            >
              <item.icon size={20} style={item.color ? { color: item.color } : {}} />
              <div className="sipotm-nav-item-content">
                <span className="sipotm-nav-label">{item.label}</span>
              </div>
            </button>
          ))}
        </nav>

        <div className="sipotm-sidebar-footer">
          <button
            className="sipotm-nav-item logout"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut size={20} />
            <div className="sipotm-nav-item-content">
              <span className="sipotm-nav-label">Logout</span>
            </div>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="sipotm-layout-content">
        {/* Top Header */}
        <header className="sipotm-top-header">
          <div className="sipotm-header-title">{getPageTitle()}</div>

          <div className="sipotm-header-actions">
            <NotificationsPanel />
            
            <div className="sipotm-user-menu">
              <button
                className="sipotm-user-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="sipotm-user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span className="sipotm-user-name">{user?.name || user?.email}</span>
              </button>

              {showUserMenu && (
                <div className="sipotm-user-dropdown">
                  <div className="sipotm-dropdown-header">
                    <User size={18} />
                    <div>
                      <div className="sipotm-dropdown-name">{user?.name || 'SIP/OTM User'}</div>
                      <div className="sipotm-dropdown-email">{user?.email}</div>
                    </div>
                  </div>
                  <hr />
                  <button
                    className="sipotm-dropdown-item logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="sipotm-page-content">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}

export default SIPOTMLayout;
